import { pouches_db, syncStatus } from "../config"; //localStorageCommon
import PouchDB from "pouchdb-browser";
import { logger } from "../functions/logger";

export default function usePouchQueues(dbName) {
  const getAllDocs = async (options) => {
    try {
      const db = new PouchDB(dbName);
      const result = await db.allDocs({ include_docs: true, ...options });
      return result.rows.map((row) => row.doc);
    } catch (error) {
      console.error("Error fetching all docs:", error);
      return [];
    }
  };

  const createNewQueue = async (data, options = {}) => {
    // const localObject = localStorage.getItem(localStorageCommon);

    // Read the offline "common" doc to enrich the queued change. It can be MISSING
    // after an IndexedDB eviction (offline_db wiped) — don't let that crash the
    // queue write: fall back to whatever creditor_id/collect_date the caller already
    // put in postData. (recoverCommon also restores this doc; this is defense-in-depth.)
    let common = null;
    try {
      const common_db = new PouchDB(pouches_db.OFFLINE_DATA);
      common = await common_db.get("common");
    } catch (e) {
      // offline_db "common" not found -> proceed without enrichment
    }

    let newPostData = data.postData;
    if (common) {
      const { creditor_id, collect_date, user_id } = common;
      newPostData = { ...data.postData, creditor_id, collect_date, user_id };
    }

    try {
      let newData = { ...data, postData: newPostData };
      newData.triesCount = 0;
      newData.queuedTime = Date.now();
      newData.replayAfter = Date.now();

      const db = new PouchDB(dbName);
      const res = await db.put(newData, options);
      logger.debug(`${dbName}.queued`, "Change queued", { entity: dbName, entityId: newData?._id });
      return res;
    } catch (err) {
      logger.error(`${dbName}.queue_failed`, "Error queueing change", {
        entity: dbName,
        context: { error: String(err?.message || err) },
      });
      console.log("Error creating create doc", err);
      throw new Error(err);
    }
  };

  const putSertQueue = async (data, options = {}) => {
    try {
      const db = new PouchDB(dbName);
      const doc = await getDocById(data._id);
      if (doc) return await db.put({ ...doc, ...data }, { force: true, ...options });

      return await createNewQueue(data, { force: true, ...options });
    } catch (error) {
      logger.error(`${dbName}.queue_failed`, "Error queueing change", {
        entity: dbName,
        entityId: data?._id,
        context: { error: String(error?.message || error) },
      });
      console.log("Error in putSertQueue function:", error);
      throw new Error(error);
    }
  };

  const putSertHistory = async (data, options = {}) => {
    const newDoc = { ...data, syncStatus: syncStatus.PENDING };

    try {
      const db = new PouchDB(dbName);
      const doc = await getDocById(data._id);
      if (doc) return await db.put({ ...doc, ...newDoc }, options);

      return await db.put(newDoc, options);
    } catch (error) {
      logger.error(`${dbName}.history_failed`, "Error writing change history", {
        entity: dbName,
        entityId: data?._id,
        context: { error: String(error?.message || error) },
      });
      console.log("Error in putSertHistory function:", error);
      throw new Error(error);
    }
  };

  const getDocById = async (docId, options = {}) => {
    try {
      const db = new PouchDB(dbName);
      return await db.get(docId, options);
    } catch (err) {
      // console.log("Error fetch single doc", err);
      return null;
    }
  };

  const putDoc = async (doc, options = {}) => {
    try {
      const db = new PouchDB(dbName);
      return await db.put(doc, options);
    } catch (err) {
      logger.error(`${dbName}.save_failed`, "Error saving document", {
        entity: dbName,
        entityId: doc?._id,
        context: { error: String(err?.message || err) },
      });
      console.log("error in putDOc:", err);
      throw new Error(err);
    }
  };

  const compact = async () => {
    try {
      const db = new PouchDB(dbName);
      return await db.compact();
    } catch (err) {
      console.log(err);
    }
  };

  const deleteDoc = async (docId) => {
    try {
      const db = new PouchDB(dbName);
      const doc = await db.get(docId);
      doc._deleted = true;

      const result = await db.put(doc);
      logger.debug(`${dbName}.deleted`, "Queue document deleted", { entity: dbName, entityId: docId });
      return result;
    } catch (err) {
      logger.error(`${dbName}.delete_failed`, "Error deleting document", {
        entity: dbName,
        entityId: docId,
        context: { error: String(err?.message || err) },
      });
      console.log("Error while deleting doc:", err);
      throw new Error(err);
    }
  };

  const getDatabaseInfo = async () => {
    try {
      const db = new PouchDB(dbName);
      return await db.info();
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  const deleteDatabase = async () => {
    try {
      const db = new PouchDB(dbName);
      return await db.destroy(dbName);
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  return {
    compact,
    getDocById,
    putSertQueue,
    putSertHistory,
    getAllDocs,
    putDoc,
    deleteDoc,
    getDatabaseInfo,
    deleteDatabase,
  };
}
